import React from 'react';
import { Box, Typography, LinearProgress } from '@mui/material';
import HelpMark from '../help/HelpMark';

// "137 / 200 issued this month" with a bar that turns amber at 80 % and red
// at 100 %. `usage` is one entry of the /me/entitlements features map:
// { kind, enabled, limit, used, remaining }. Unlimited → plain count, no bar.
export const meterTone = (used, limit) => {
  if (limit == null || limit <= 0) return 'ok';
  const ratio = used / limit;
  if (ratio >= 1) return 'critical';
  if (ratio >= 0.8) return 'warning';
  return 'ok';
};

const COLOR = { ok: 'success', warning: 'warning', critical: 'error' };

// `helpId` (optional, step 9): renders a filled HelpMark right after the
// label — the meter's own "?" per design_handoff_help_system/README.md.
// Omit it and this component renders exactly as it did before step 9.
const UsageMeter = ({ label, usage, unlimitedLabel, compact, helpId }) => {
  if (!usage || usage.used == null) return null;
  const { used, limit } = usage;
  const tone = meterTone(used, limit);
  const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;

  return (
    <Box sx={{ my: compact ? 0.5 : 1 }} data-testid="usage-meter" data-tone={tone}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mb: 0.5 }}>
        <Typography variant={compact ? 'caption' : 'body2'} color="text.secondary" noWrap>
          {label}
          {helpId && <HelpMark id={helpId} variant="filled" />}
        </Typography>
        <Typography
          variant={compact ? 'caption' : 'body2'}
          fontWeight={600}
          sx={{ fontVariantNumeric: 'tabular-nums', color: tone === 'ok' ? 'text.primary' : `${COLOR[tone]}.main` }}
        >
          {limit == null ? `${used} · ${unlimitedLabel}` : `${used} / ${limit}`}
        </Typography>
      </Box>
      {limit != null && (
        <LinearProgress
          variant="determinate"
          value={pct}
          color={COLOR[tone]}
          aria-label={label}
          sx={{ height: compact ? 5 : 7, borderRadius: 999 }}
        />
      )}
    </Box>
  );
};

export default UsageMeter;
